import React, { useContext, useState } from "react";
import { ReportParamsContext } from "../../context/ReportParamsContext";
import { getCallTime } from "../../API";

function formatDuration(ms) {
  if (!ms || ms <= 0) return "—";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

function repFlag(avg, companyAvg, threshold) {
  if (companyAvg === 0) return "neutral";
  if (avg > companyAvg * (1 + threshold)) return "high";
  if (avg < companyAvg * (1 - threshold)) return "low";
  return "ok";
}

function FlagBadge({ flag }) {
  if (flag === "high") return <span className="badge bg-warning text-dark ms-1">⬆ Too high</span>;
  if (flag === "low")  return <span className="badge bg-danger ms-1">⬇ Too low</span>;
  if (flag === "ok")   return <span className="badge bg-success ms-1">✓ Normal</span>;
  return null;
}

function TimeBar({ avgMs, companyAvgMs }) {
  // Cap bar at 2× company avg
  const cap = companyAvgMs > 0 ? companyAvgMs * 2 : avgMs * 2 || 1;
  const repWidth = Math.min((avgMs / cap) * 100, 100);
  const refLine  = Math.min((companyAvgMs / cap) * 100, 100);
  const flag     = repFlag(avgMs, companyAvgMs, 0); // just for color
  const barColor = avgMs > companyAvgMs * 1.001
    ? (avgMs > companyAvgMs * 1.2 ? "bg-warning" : "bg-warning")
    : avgMs < companyAvgMs * 0.999 && companyAvgMs > 0
      ? (avgMs < companyAvgMs * 0.8 ? "bg-danger" : "bg-info")
      : "bg-success";

  return (
    <div className="d-flex align-items-center gap-2" style={{ minWidth: 220 }}>
      <span className="fw-semibold" style={{ minWidth: 68, textAlign: "right", whiteSpace: "nowrap" }}>
        {formatDuration(avgMs)}
      </span>
      <div className="position-relative flex-grow-1" style={{ height: 10 }}>
        <div className="progress w-100" style={{ height: 10, borderRadius: 4 }}>
          <div className={`progress-bar ${barColor}`} style={{ width: `${repWidth}%`, borderRadius: 4 }} />
        </div>
        {companyAvgMs > 0 && (
          <div
            className="position-absolute top-0"
            style={{ left: `${refLine}%`, width: 2, height: 10, background: "#333", borderRadius: 1 }}
            title={`Group avg: ${formatDuration(companyAvgMs)}`}
          />
        )}
      </div>
      {companyAvgMs > 0 && (
        <span className="text-muted" style={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>
          avg {formatDuration(companyAvgMs)}
        </span>
      )}
    </div>
  );
}

export default function CallTimeReport() {
  const [{ fromDate, toDate, representatives, manufacturers }] = useContext(ReportParamsContext);

  const [flagPct,      setFlagPct]      = useState(20);
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [expandedReps, setExpandedReps] = useState(new Set());

  const handleGenerate = async () => {
    if (!fromDate || !toDate) { setError("Please select at least one month in the parameters bar."); return; }
    setLoading(true);
    setError(null);
    try {
      const result = await getCallTime({
        fromDate,
        toDate,
        flagThreshold: flagPct / 100,
        representativeIds: representatives.map((r) => r.id),
        manufacturerIds: manufacturers.map((m) => m.id),
      });
      setData(result);
      setExpandedReps(new Set());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleRep = (repId) =>
    setExpandedReps((prev) => {
      const next = new Set(prev);
      next.has(repId) ? next.delete(repId) : next.add(repId);
      return next;
    });

  const tooHighCount = data?.reps.filter((r) => r.tooHigh).length ?? 0;
  const tooLowCount  = data?.reps.filter((r) => r.tooLow).length  ?? 0;

  return (
    <div className="flex-grow-1 overflow-auto">
      <div className="container-fluid py-4 px-4">

        <div className="card shadow-sm mb-4">
          <div className="card-body p-3">
            <div className="d-flex align-items-center gap-4 flex-wrap">
              <div>
                <label className="form-label small fw-medium mb-1 d-block">
                  Flag threshold (±% from group avg)
                </label>
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="range" className="form-range" min={5} max={50} step={5}
                    value={flagPct}
                    onChange={(e) => setFlagPct(Number(e.target.value))}
                    style={{ width: 140 }}
                  />
                  <span className="badge bg-secondary">±{flagPct}%</span>
                </div>
              </div>
              <div className="ms-auto">
                <button className="btn btn-primary" onClick={handleGenerate} disabled={loading}>
                  {loading ? "Loading…" : "Generate Report"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {data && (
          <>
            {/* Summary cards */}
            <div className="row g-3 mb-3">
              <div className="col-sm-3">
                <div className="card text-center shadow-sm h-100">
                  <div className="card-body py-3">
                    <div className="fs-3 fw-bold">{formatDuration(data.companyAvgMs)}</div>
                    <div className="text-muted small">Group avg call time</div>
                  </div>
                </div>
              </div>
              <div className="col-sm-3">
                <div className="card text-center shadow-sm h-100">
                  <div className="card-body py-3">
                    <div className="fs-3 fw-bold">{data.companyVisitCount}</div>
                    <div className="text-muted small">
                      Timed visits · {formatDuration(data.companyTotalMs)} total
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-sm-3">
                <div className={`card text-center shadow-sm h-100 ${tooHighCount > 0 ? "border-warning" : ""}`}>
                  <div className="card-body py-3">
                    <div className={`fs-3 fw-bold ${tooHighCount > 0 ? "text-warning" : "text-success"}`}>
                      {tooHighCount}
                    </div>
                    <div className="text-muted small">Rep{tooHighCount !== 1 ? "s" : ""} spending too long (&gt; +{flagPct}%)</div>
                  </div>
                </div>
              </div>
              <div className="col-sm-3">
                <div className={`card text-center shadow-sm h-100 ${tooLowCount > 0 ? "border-danger" : ""}`}>
                  <div className="card-body py-3">
                    <div className={`fs-3 fw-bold ${tooLowCount > 0 ? "text-danger" : "text-success"}`}>
                      {tooLowCount}
                    </div>
                    <div className="text-muted small">Rep{tooLowCount !== 1 ? "s" : ""} spending too little (&lt; -{flagPct}%)</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="d-flex align-items-center gap-4 mb-3 small text-muted flex-wrap">
              <span><span className="badge bg-success me-1">✓ Normal</span> Within ±{flagPct}% of group avg</span>
              <span><span className="badge bg-warning text-dark me-1">⬆ Too high</span> &gt; +{flagPct}% (inefficiency / time theft risk)</span>
              <span><span className="badge bg-danger me-1">⬇ Too low</span> &lt; -{flagPct}% (under-servicing risk)</span>
              <span>
                <span style={{ display: "inline-block", width: 2, height: 10, background: "#333", verticalAlign: "middle" }} className="me-1" />
                = group average reference
              </span>
            </div>

            {data.reps.length === 0 ? (
              <div className="alert alert-info">
                No timed visits found. Store call timing data may not be recorded yet.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered mb-0" style={{ fontSize: "0.85rem" }}>
                  <thead className="table-dark">
                    <tr>
                      <th style={{ width: 24 }} />
                      <th>Representative</th>
                      <th style={{ minWidth: 300 }}>Avg call time</th>
                      <th style={{ whiteSpace: "nowrap" }}>Status</th>
                      <th className="text-end">Visits</th>
                      <th className="text-end" style={{ whiteSpace: "nowrap" }}>Total time</th>
                      <th className="text-end">Clients</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.reps.map((rep) => {
                      const flag = repFlag(rep.avgCallMs, data.companyAvgMs, data.flagThreshold);
                      const rowClass = rep.tooHigh ? "table-warning" : rep.tooLow ? "table-danger" : "";
                      return (
                        <React.Fragment key={rep.repId}>
                          <tr
                            className={rowClass}
                            style={{ cursor: rep.manufacturers.length > 0 ? "pointer" : "default" }}
                            onClick={() => rep.manufacturers.length > 0 && toggleRep(rep.repId)}
                          >
                            <td className="text-center fw-bold" style={{ userSelect: "none" }}>
                              {rep.manufacturers.length > 0
                                ? expandedReps.has(rep.repId) ? "▾" : "▸"
                                : ""}
                            </td>
                            <td className="fw-semibold">{rep.repName || "(Unknown)"}</td>
                            <td>
                              <TimeBar avgMs={rep.avgCallMs} companyAvgMs={data.companyAvgMs} />
                            </td>
                            <td><FlagBadge flag={flag} /></td>
                            <td className="text-end">{rep.visitCount}</td>
                            <td className="text-end text-muted" style={{ whiteSpace: "nowrap" }}>
                              {formatDuration(rep.totalMs)}
                            </td>
                            <td className="text-end">{rep.manufacturers.length}</td>
                          </tr>

                          {expandedReps.has(rep.repId) && rep.manufacturers.map((mfr) => (
                            <tr key={mfr.manufacturerId} className="table-light" style={{ fontSize: "0.8rem" }}>
                              <td />
                              <td className="ps-4 text-muted">{mfr.manufacturerName}</td>
                              <td>
                                <TimeBar avgMs={mfr.avgMsPerCall} companyAvgMs={mfr.companyAvgMsPerCall} />
                              </td>
                              <td className="text-muted" style={{ fontSize: "0.75rem" }}>
                                avg {formatDuration(mfr.companyAvgMsPerCall)} / call (group)
                              </td>
                              <td />
                              <td className="text-end text-muted" style={{ whiteSpace: "nowrap" }}>
                                {formatDuration(mfr.totalMs)}
                              </td>
                              <td />
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
